import React, { useState, useEffect, useCallback } from 'react';
import { listJobs } from '../services/api';
import { classifyError, getSystemErrorLabel } from '../utils/errorClassifier';

/**
 * SystemErrorBanner Component
 *
 * Independently fetches recent failed jobs and displays a collapsible
 * alert banner when system-level errors are actively occurring.
 *
 * Only shows errors from the last 15 minutes — once the issue resolves
 * and no new system failures appear, the banner auto-clears.
 */

const LOOKBACK_MS = 15 * 60 * 1000; // 15 minutes

interface SystemError {
  label: string;
  jobs: { jobId: string; filename: string; error: string; site: string; press: string }[];
}

export const SystemErrorBanner: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [systemErrors, setSystemErrors] = useState<SystemError[]>([]);
  const [totalAffected, setTotalAffected] = useState(0);

  const fetchSystemErrors = useCallback(async () => {
    try {
      const response = await listJobs({ status: 'failed' }, 1, 50);
      const cutoff = Date.now() - LOOKBACK_MS;
      const errors: SystemError[] = [];
      const labelMap = new Map<string, SystemError>();
      let affected = 0;

      for (const job of response.jobs) {
        if (!job.error) continue;
        // Only consider jobs that failed recently
        const failTime = job.endTime || job.startTime;
        if (failTime < cutoff) continue;
        if (classifyError(job.error) !== 'system') continue;

        affected++;
        const label = getSystemErrorLabel(job.error);
        let entry = labelMap.get(label);
        if (!entry) {
          entry = { label, jobs: [] };
          labelMap.set(label, entry);
          errors.push(entry);
        }
        entry.jobs.push({
          jobId: job.jobId,
          filename: job.filename,
          error: job.error,
          site: job.site,
          press: job.press,
        });
      }

      setSystemErrors(errors);
      setTotalAffected(affected);
    } catch {
      // Silently fail — don't block the dashboard
    }
  }, []);

  useEffect(() => {
    fetchSystemErrors();
    const interval = setInterval(fetchSystemErrors, 30000);
    return () => clearInterval(interval);
  }, [fetchSystemErrors]);

  if (systemErrors.length === 0) return null;

  return (
    <div
      data-testid="system-error-banner"
      className="mx-4 mb-4 bg-red-50 border border-red-300 rounded-lg overflow-hidden"
    >
      <button
        data-testid="system-error-toggle"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-red-100 transition-colors"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-semibold text-red-800">
            {systemErrors.length} system {systemErrors.length === 1 ? 'issue' : 'issues'} detected
          </span>
          <span className="text-xs text-red-600">
            ({totalAffected} affected {totalAffected === 1 ? 'job' : 'jobs'} in last 15 min)
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-red-600 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div data-testid="system-error-details" className="px-4 pb-3 space-y-3">
          {systemErrors.map((err) => (
            <div key={err.label} className="border-t border-red-200 pt-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-red-800">{err.label}</span>
                <span className="text-xs bg-red-200 text-red-800 px-1.5 py-0.5 rounded-full">
                  {err.jobs.length}
                </span>
              </div>
              <div className="space-y-1">
                {err.jobs.slice(0, 5).map((j) => (
                  <div key={j.jobId} className="text-xs text-red-700 flex gap-2">
                    <span className="font-mono truncate max-w-[200px]" title={j.filename}>
                      {j.filename}
                    </span>
                    <span className="text-red-500">
                      {j.site}{j.press}
                    </span>
                  </div>
                ))}
                {err.jobs.length > 5 && (
                  <div className="text-xs text-red-500 italic">
                    and {err.jobs.length - 5} more...
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SystemErrorBanner;
